import { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { HTTP_STATUS_CODE_PREFIX_REGEX } from '../../consts/url';
import { InviteOrganizationError } from '../httpClients/PlatformClient/api.schema';
import { QUERY_KEYS } from '../queries/consts';

/**
 * Invalidates all cross-repo relationship caches (edges + overview).
 * Must be called after any edge write so the side-panel and graph/table stay in sync.
 */
export const invalidateCrossRepoRelations = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CROSS_REPO_EDGES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CROSS_REPO_OVERVIEW] });
};

import { CSV_SPECIFIC_ERROR_REGEXP } from './consts';

/**
 * Type guard to check if an error is of type InviteOrganizationError with invalid entries
 * @param err - The error object to check
 * @returns true if the error is an InviteOrganizationError with detailed_errors array, false otherwise
 */
export const isInviteError = (err: unknown): err is InviteOrganizationError =>
  typeof err === 'object' &&
  err !== null &&
  'detailedErrors' in err &&
  Array.isArray((err as any).detailedErrors);

/**
 * Detects a raw serialized backend error dump (e.g. FastAPI/Pydantic
 * `RequestValidationError`, which stringifies to `[{'type': 'value_error', ...}]`).
 * Such strings are developer-facing noise and must never be shown in a toast.
 */
const RAW_ERROR_DUMP_REGEX = /^[[{]|'(?:type|loc|msg|ctx)':|value_error/;

/**
 * Gets the error message from the server
 * @param err - The error object to get the error message from
 * @param fallbackMessage - The fallback message to use if there is no (usable) error message from the server
 * @returns A user-facing server message when present and clean, otherwise the fallback
 */
export const getErrorToastContent = (err: unknown, fallbackMessage: string) => {
  const errorFromServer = isAxiosError(err)
    ? err.response?.data?.error?.message
    : undefined;

  if (typeof errorFromServer !== 'string') {
    return fallbackMessage;
  }

  const cleaned = errorFromServer
    .replace(HTTP_STATUS_CODE_PREFIX_REGEX, '')
    .trim();

  if (!cleaned.length || RAW_ERROR_DUMP_REGEX.test(cleaned)) {
    return fallbackMessage;
  }

  return cleaned;
};

/** Gets a specific error message based on known patterns
 * @param err - The error object to analyze
 * @param fallbackMessage - The fallback message to use if no specific message is found
 * @returns A specific error message or the fallback message
 */
export const getSpecificErrorMessage = (
  err: any,
  fallbackMessage: string,
): string => {
  const errorFromServer = err.response?.data?.error?.message;

  if (typeof errorFromServer === 'string') {
    // Check for CSV specific error pattern
    const csvMatch = errorFromServer.match(CSV_SPECIFIC_ERROR_REGEXP);

    if (csvMatch) {
      return errorFromServer.replace(HTTP_STATUS_CODE_PREFIX_REGEX, '');
    }
  }

  return fallbackMessage;
};
